import { useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import { Logo } from '@/components/ui/Logo'
import type { User } from '@/types'
import { useApiStatus } from '@/contexts/ApiContext'
import { visibleNavGroups, useNavSections, type NavItem } from '@/lib/nav'
import TeamSeatLinks from '@/components/layout/TeamSeatLinks'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

function formatTokenCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`
  return String(n)
}

function NavRow({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  return (
    <NavLink
      to={item.to}
      end
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-150 relative',
          collapsed ? 'justify-center p-2.5' : 'px-3 py-1.5',
          isActive
            ? 'text-brand-500 bg-brand-500/5'
            : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <div className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-brand-500" />}
          <item.icon className="w-4 h-4 shrink-0" />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  )
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { pathname } = useLocation()
  const { connected } = useApiStatus()
  const [currentUser, setCurrentUser] = useState<User | null>(null)

  useEffect(() => {
    api.getMe()
      .then((result) => {
        if (result?.user) setCurrentUser(result.user as unknown as User)
      })
      .catch(() => {})
  }, [])

  const isAdmin = currentUser?.role === 'admin'
  const groups = useMemo(() => visibleNavGroups(isAdmin), [isAdmin])
  const { isOpen, toggle } = useNavSections(pathname, groups)

  const tokenUsed = currentUser?.org.tokensUsed ?? 0
  const tokenLimit = currentUser?.org.tokensLimit ?? 1
  const tokenPercent = Math.min(100, Math.round((tokenUsed / tokenLimit) * 100))
  const tokenLabel = `${formatTokenCount(tokenUsed)} / ${formatTokenCount(tokenLimit)} tokens`

  return (
    <aside
      className={cn(
        'flex h-full flex-col bg-zinc-900 border-r border-zinc-800 transition-[width] duration-300 ease-in-out pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
        collapsed ? 'w-16' : 'w-60'
      )}
    >
      {/* Brand + collapse toggle */}
      <div className={cn('flex items-center h-14 px-3 shrink-0', collapsed ? 'justify-center' : 'justify-between')}>
        {!collapsed && (
          <NavLink to="/home" className="flex items-center gap-2.5 min-w-0" aria-label="Forge — home">
            <Logo variant="wordmark" size="sm" />
          </NavLink>
        )}
        <button
          onClick={onToggle}
          className="flex items-center justify-center w-8 h-8 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/50 transition-colors shrink-0"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar  [' : 'Collapse sidebar  ['}
        >
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 pb-3 space-y-3">
        {groups.map((group, gi) => {
          const hasHeader = group.label !== ''
          const defaultOpen = group.defaultOpen !== false
          const open = !group.collapsible || isOpen(group.label, defaultOpen)

          return (
            <div key={group.label || `group-${gi}`}>
              {hasHeader && (collapsed ? (
                group.collapsible && group.icon ? (
                  <button
                    type="button"
                    onClick={() => toggle(group.label, defaultOpen)}
                    aria-expanded={open}
                    aria-label={group.label}
                    title={group.label}
                    className={cn(
                      'flex items-center justify-center w-full p-2 mb-0.5 rounded-md transition-colors hover:bg-zinc-800/30',
                      open ? 'text-zinc-300' : 'text-zinc-500 hover:text-zinc-300',
                    )}
                  >
                    <group.icon className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="mx-3 mb-2 border-t border-zinc-800" />
                )
              ) : (
                <button
                  type="button"
                  onClick={() => group.collapsible && toggle(group.label, defaultOpen)}
                  aria-expanded={group.collapsible ? open : undefined}
                  className={cn(
                    'flex items-center justify-between w-full px-3 py-1.5',
                    group.collapsible ? 'rounded-md hover:bg-zinc-800/30 transition-colors' : 'cursor-default',
                  )}
                >
                  <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                    {group.label}
                  </span>
                  {group.collapsible && (
                    <ChevronDown
                      className={cn('w-3 h-3 text-zinc-600 transition-transform duration-200', !open && '-rotate-90')}
                    />
                  )}
                </button>
              ))}

              {open && (
                <div className="flex flex-col gap-0.5">
                  {group.items.map((item) => (
                    <NavRow key={item.to} item={item} collapsed={collapsed} />
                  ))}
                </div>
              )}

              {/* Team sits right under Home/Approvals — it's where the work
                  comes from — but stays compact until opened. */}
              {gi === 0 && (
                <div className="mt-3">
                  <TeamSeatLinks collapsed={collapsed} isAdmin={isAdmin} />
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* Footer: connection + token budget. Account actions live in the
          header avatar menu. */}
      <div className="shrink-0 border-t border-zinc-800">
        {collapsed ? (
          <div className="flex justify-center py-3" title={`${connected ? 'API connected' : 'Demo mode'} · ${tokenLabel}`}>
            <span className={cn('h-2 w-2 rounded-full', connected ? 'bg-brand-500' : 'bg-zinc-600')} />
          </div>
        ) : (
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Tokens</span>
              <span className="text-[11px] text-zinc-400 tabular-nums">
                {formatTokenCount(tokenUsed)} / {formatTokenCount(tokenLimit)}
              </span>
            </div>
            <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all duration-500', tokenPercent > 80 ? 'bg-brand-600' : 'bg-brand-500')}
                style={{ width: `${tokenPercent}%` }}
              />
            </div>
            <div className="flex items-center gap-1.5 mt-2">
              <span className={cn('h-1.5 w-1.5 rounded-full', connected ? 'bg-brand-400' : 'bg-zinc-600')} />
              <span className="text-[10px] text-zinc-600">{connected ? 'API Connected' : 'Demo Mode'}</span>
            </div>
          </div>
        )}
      </div>
    </aside>
  )
}
